// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import {
  type FacilityId,
  type DateOnly,
  today,
  type WorkCellTask,
  type Task,
  type TaskTimeEntry,
  type RenderableUser,
  useEvolveGet,
  useEvolvePaginatedGet,
  useEvolvePost,
  useEvolvePut,
} from '@evolvemep/api-ts';
import { Button, Flex, Stack, Text } from '@mantine/core';
import { useForm } from '@mantine/form';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { DateInputWrapped } from 'components/Mantine/DateInputWrapped';
import { DurationInput } from 'components/Mantine/DurationInput';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { useFormModal, FormModal } from 'components/Mantine/Modals/FormModal';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import type { WithNullable, WithOptional, WithRequiredNotNull } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { AssignUserToTaskPopover, SelectableWorkCellTaskItem } from 'modules/Shop/TimeTracking/AssignUserToTaskPopover';
import { ConfirmAssignUserToTaskPopover } from 'modules/Shop/TimeTracking/ConfirmAssignUserToTaskPopover';

type FormValues = {
  user: RenderableUser;
  workCellTask: WithOptional<
    Pick<WorkCellTask, 'taskId' | 'taskName' | 'workOrderName' | 'projectName' | 'assignees'>,
    'assignees'
  >;
  totalSeconds: number;
  workDate: DateOnly;
};

type Props = {
  facilityId: FacilityId;
  taskTimeEntryId?: TaskTimeEntry['taskTimeEntryId'];
  defaultValue?: Partial<FormValues>;
  onTimeEntrySaved: (entry: TaskTimeEntry) => void;
};

export const CreateEditTimeEntryForm = ({ facilityId, onTimeEntrySaved, defaultValue, taskTimeEntryId }: Props) => {
  const { setDisabled } = useWrappedModal();
  const paginatedGetUsers = useEvolvePaginatedGet(`admin/user`, {
    defaultConfig: {
      params: {
        facilityId,
      },
    },
  });

  const { apiCall: createTimeEntryApiCall, loading: creating } = useEvolvePost(`shop/task/timeEntry`);
  const { apiCall: updateTimeEntryApiCall, loading: updating } = useEvolvePut(`shop/task/timeEntry/:taskTimeEntryId`);
  const saving = creating || updating;

  const form = useForm<WithNullable<FormValues>>({
    initialValues: {
      workCellTask: null,
      user: null,
      totalSeconds: null,
      workDate: today(),
      ...defaultValue,
    },
    validate: {
      workCellTask: (v) => !v,
      user: (v) => !v,
      totalSeconds: (v) => !v,
      workDate: (v) => !v,
    },
    enhanceGetInputProps: () => ({ disabled: saving }),
  });

  const {
    setDefaultOpts: setDefaultOptsWorkCellTasks,
    reset,
    ...paginatedGetTasks
  } = useEvolvePaginatedGet(`shop/workCellTask`);
  const [preloadReady, setPreloadReady] = useState(false);
  useEffect(() => {
    setDefaultOptsWorkCellTasks({
      defaultConfig: {
        params: {
          facilityIds: facilityId,
          assigneeIds: form.values.user?.userId,
        },
      },
    });
    reset();
    // Don't preload tasks until the default params are set
    setPreloadReady(true);
  }, [facilityId, form.values.user?.userId, reset, setDefaultOptsWorkCellTasks]);

  const { apiCall: getTaskApiCall, loading: checkingAssignees } = useEvolveGet(`shop/task/:taskId`);

  const onSubmit = async (rawValues: typeof form.values) => {
    // Cast validated in the form
    const { user, workCellTask, ...values } = rawValues as WithRequiredNotNull<typeof rawValues>;

    const body = {
      userId: user.userId,
      taskId: workCellTask.taskId,
      ...values,
    };

    await (
      isNotNil(taskTimeEntryId)
        ? updateTimeEntryApiCall(body, {
            url: `shop/task/timeEntry/${taskTimeEntryId}`,
          })
        : createTimeEntryApiCall(body)
    ).then(onTimeEntrySaved);
  };

  useFormModal(form, onSubmit);

  const [assigningTask, setAssigningTask] = useState(false);
  const [verifyAssignUserToTask, setVerifyAssignUserToTask] = useState<Pick<
    Task,
    'taskId' | 'assignees' | 'taskName'
  > | null>(null);

  useEffect(() => {
    setDisabled(checkingAssignees || isNotNil(verifyAssignUserToTask));
  }, [checkingAssignees, setDisabled, verifyAssignUserToTask]);

  return (
    <>
      <SearchableSelect<'admin/user', RenderableUser, 'userId'>
        label="Shop Member"
        idKey="userId"
        paginatedGet={paginatedGetUsers}
        getItemLabel={(u) => validateUserNameOrEmail(u)}
        required
        {...form.getInputProps('user')}
        onChange={async (user) => {
          // oxlint-disable-next-line typescript/no-unsafe-call
          form.getInputProps('user').onChange(user);
          const task = form.values.workCellTask;
          if (isNotNil(user) && isNotNil(task)) {
            const assignees = task.assignees ?? (await getTaskApiCall({ url: `shop/task/${task.taskId}` })).assignees;
            if (!assignees.some((a) => a.user.userId === user.userId)) {
              setVerifyAssignUserToTask({
                ...task,
                assignees,
              });
            }
          }
        }}
        disabled={!!taskTimeEntryId || saving}
      />
      <Flex gap="md" justify="space-between">
        <DateInputWrapped
          label="Date of Work"
          required
          flex={1}
          maxDate={today()}
          {...form.getInputProps('workDate')}
        />
        <DurationInput label="Work Hours" required max={24 * 60 * 60} {...form.getInputProps('totalSeconds')} />
      </Flex>
      <Stack gap="xs">
        <Flex>
          <SearchableSelect<'shop/workCellTask', WorkCellTask, 'taskId'>
            label="Task"
            idKey="taskId"
            paginatedGet={{ reset, ...paginatedGetTasks }}
            getItemLabel={(t) => `${t.taskName} (${t.workOrderName})`}
            renderOption={SelectableWorkCellTaskItem}
            required
            preloadData={preloadReady}
            w="100%"
            clearable
            rightSection={
              isNil(form.values.workCellTask) ? (
                <InfoIcon label="A shop member can only have time entries for tasks they are assigned to." />
              ) : undefined
            }
            {...form.getInputProps('workCellTask')}
            loading={checkingAssignees}
            disabled={!form.values.user || saving || checkingAssignees}
          />
          {isNotNil(form.values.user) && (
            <AssignUserToTaskPopover
              user={form.values.user}
              opened={assigningTask}
              onClose={() => setAssigningTask(false)}
              onSubmit={(workCellTask) => {
                setAssigningTask(false);
                form.setFieldValue('workCellTask', workCellTask);
              }}
              facilityId={facilityId}
            />
          )}
          <ConfirmAssignUserToTaskPopover
            user={form.values.user}
            onConfirm={() => setVerifyAssignUserToTask(null)}
            onCancel={() => {
              setVerifyAssignUserToTask(null);
              form.setFieldValue('workCellTask', null);
            }}
            task={verifyAssignUserToTask}
          />
        </Flex>
        {isNotNil(form.values.user) && !form.values.user.isDeleted && (
          <Text fz="sm" c="dimmed">
            {"Don't see the task you're looking for? "}
            <Text className="link" onClick={() => setAssigningTask(true)} span>
              Click here
            </Text>{' '}
            to assign this shop member to another task.
          </Text>
        )}
      </Stack>
    </>
  );
};

export const CreateTimeEntryModal = (props: Props) => {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <Button onClick={() => setOpened(true)} leftSection={<EvolveIcon icon="Add" size="sm" />}>
        New entry
      </Button>
      <FormModal
        opened={opened}
        onClose={() => setOpened(false)}
        title="New time entry"
        confirmationText="Create"
        type="submit"
      >
        <CreateEditTimeEntryForm {...props} />
      </FormModal>
    </>
  );
};
